import debounce from 'lodash/debounce';
import { gzip, inflate } from 'pako';
import { getProjectState, setProjectState, _useProjectStore } from '../store';
import { convertLegacyCanvasData } from './canvas';
import { isArrayEqual } from '../utils/base';
import { tryResumeTasks } from './process';
import { eventBus } from './eventBus';
import { formatNode } from './flow';
import { FlOW_PROJECT_INFO, UNIT_TYPE } from '../components/flow/constants';
import { request } from '@shakkerai/utils';
import { getProjectId } from '../utils';
import { appendSearchParamsToUrl } from '@shakkerai/utils';
import { isOversea } from '../constants';

const initCanvasData = async () => {
  try {
    const response = await request<CommonResponse<{ canvas: string; validProjectId: boolean }>, { projectId: string }>(
      isOversea ? '/gateway/sd-api/gen/tool/canvas/query' : '/api/canva/project/queryProject',
      {
        projectId: getProjectId(),
      },
    );
    if (response?.code !== 0) throw new Error(response.msg);

    let cloudData = null;
    // 新建projectId的场景validProjectId为null, 这里兜底下, 只有明确返回false，才认为项目id无效
    const validProjectId = isOversea ? true : response?.data?.validProjectId ?? true;
    const canvas = (isOversea ? response?.data ?? '' : response?.data?.canvas ?? '') as string;

    if (canvas.startsWith('SHAKKERDATA://')) {
      const compressedData = canvas.slice('SHAKKERDATA://'.length);
      const base64decodedData = atob(compressedData);
      const uint8Array = new Uint8Array(base64decodedData.length);

      for (let i = 0; i < base64decodedData.length; i++) {
        uint8Array[i] = base64decodedData.charCodeAt(i);
      }

      cloudData = JSON.parse(inflate(uint8Array, { to: 'string' }));
    } else if (canvas) {
      cloudData = JSON.parse(response.data.canvas);
    }

    if (cloudData) {
      const convertedData = convertLegacyCanvasData({ ...cloudData });

      convertedData.__initialized = true;
      convertedData.__skipSaving = true;
      convertedData.validProjectId = validProjectId;

      setProjectState(convertedData);
    } else {
      throw new Error('Invalid cloud data');
    }
  } catch (error) {
    console.log(`[DEBUG] initCanvasData error:`, error);
    setProjectState({
      __initialized: true,
      __skipSaving: true,
    });
  }
};

// 需要持久化的字段
const persistedCanvasFields = ['elements', 'tasks', 'viewport'];

const saveCanvasData = async () => {
  let coreData = getProjectState();
  if (!coreData.__initialized || coreData.__skipSaving) return;

  const serializedData = {
    __version: coreData.__version,
  };

  // TODO 这里后面还可以做个时序控制，在前一个请求返回前不再次发请求

  persistedCanvasFields.forEach((key) => {
    serializedData[key] = coreData[key];
  });
  const rawString = JSON.stringify(serializedData);
  const deflatedData = gzip(rawString);
  const deflatedDataArray = Array.prototype.slice.call(deflatedData);

  let string = '';

  // TODO 优化下for in 循环
  for (let i in deflatedDataArray) {
    string += String.fromCharCode(deflatedDataArray[i]);
  }

  const compressedString = btoa(string);

  try {
    const allImages = Object.values(coreData.elements).filter((element) => element.type === 'image');
    const projectCoverList = allImages.slice(-4).map((image) => image.url ?? image.originalUrl);
    const {
      data: { newCreated, projectId, validProjectId },
    } = await request<
      CommonResponse<{
        newCreated: boolean;
        projectId: string;
        validProjectId: boolean;
      }>
    >(isOversea ? '/gateway/sd-api/gen/tool/canvas/save' : '/api/canva/project/saveProject', {
      projectId: getProjectId(),
      canvas: `SHAKKERDATA://${compressedString}`,
      projectCoverList,
      picCount: allImages.length,
    });
    if (newCreated) {
      const url = appendSearchParamsToUrl({ projectId: projectId });
      // 更新url, 但不刷新页面
      window.history.pushState({}, '', url);
    }
    // 新建projectId的场景validProjectId为null, 这里兜底下, 只有明确返回false，才认为项目id无效
    setProjectState({
      validProjectId: isOversea ? true : validProjectId ?? true,
    });
  } catch (error) {
    // 什么也不做, 静默失败
  }
};

const debouncedSaveCanvasData = debounce(() => saveCanvasData(), 500);

// save保存有点频繁
const initCanvasDataPersist = async () => {
  _useProjectStore.subscribe(
    (state) => {
      return persistedCanvasFields.map((key) => state[key]);
    },
    () => {
      debouncedSaveCanvasData();
    },
    {
      equalityFn: isArrayEqual,
    },
  );
};

export const initProjectData = async () => {
  try {
    // TODO initProjectBaseData 和 initCanvasData可以并行？initFlowData对前面的接口有依赖，可以放then里？
    await initProjectBaseData();
    await initCanvasData();
    // await initFlowData();

    tryResumeTasks();
  } catch (error) {
    console.log(`[DEBUG] initProjectData error:`, error);
  }
};

export const initProjectDataPersist = async () => {
  await initCanvasDataPersist();
};

const createDefUnit = () => {
  // 手动创建第一个unit
  const generatorUnit = formatNode({ title: '生成器', type: 'generator' });
  const generatorUnitId = generatorUnit.id;
  return {
    defNodeMap: {
      [generatorUnitId]: generatorUnit,
    },
    defNodeId: generatorUnitId,
    defCurrentNodeId: generatorUnitId,
    defFlows: [],
  };
};

// 这段代码要重构下
const initProjectBaseData = async () => {
  try {
    const res = await request('/api/canva/workspace/list', {
      projectId: getProjectId(),
    });
    if (res.code === 0) {
      const data = res.data?.data;
      if (data?.length) {
        const { name, uuid, content } = data[0];
        if (content?.length) {
          const contents = JSON.parse(content || '{}');
          const flowRes = await initFlowData(uuid);
          let flows = [];
          let newNodes = {};
          let workSpaceUuid = uuid;
          if (flowRes.code === 0 && flowRes.data?.length) {
            flows = flowRes.data
              .filter((fls) => fls.content.length)
              .map((fls) => {
                const content = fls.content ? JSON.parse(fls.content) : { data: { nodes: [] } };
                return {
                  ...content,
                  workSpaceStreamUuid: fls.uuid,
                  flowName: fls.flowName,
                  flowId: content.flowId,
                  data: {
                    ...content.data,
                    nodes: content.data.nodes.map((node) => {
                      newNodes[node.id] = node;
                      return node.id;
                    }),
                  },
                };
              });
          }
          const nodeIds = (contents.nodes ?? []).map((node) => {
            newNodes[node.id] = node;
            return node.id;
          });

          let defaultUnitId = '';

          // if...else... 用于处理历史数据中用户可能把主生成器删除造成的异常case，没有主生成器就默认创建一个
          if (!nodeIds.some((id) => id.startsWith('node-generator'))) {
            const { defNodeMap, defNodeId, defCurrentNodeId, defFlows } = createDefUnit();
            newNodes = {
              ...newNodes,
              ...defNodeMap,
            };
            nodeIds.push(defNodeId);
            defaultUnitId = defCurrentNodeId;
            flows = flows.concat(defFlows);
          } else {
            defaultUnitId = nodeIds.find((id) => id.startsWith('node-generator'));
          }
          setProjectState({
            workSpaceUuid,
            projectTitle: name,
            nodeMap: {
              ...newNodes,
            },
            units: {
              nodes: [...nodeIds],
            },
            flows,
            currentNodeId: defaultUnitId ?? null,
          });
        } else {
          // 由于给内部测试加了removeFU清除所有unit和flow卡到的bug，如果没有removeFU，这里不用补这个逻辑
          const generatorUnit = formatNode({ title: '生成器', type: 'generator' });
          const generatorUnitId = generatorUnit.id;
          const initData = {
            nodeMap: {
              [generatorUnitId]: generatorUnit,
            },
            units: {
              nodes: [generatorUnitId],
            },
            flows: [],
            currentNodeId: generatorUnitId,
            workSpaceUuid: uuid,
          };
          setProjectState(initData);
        }
      } else {
        const { defNodeMap, defNodeId, defCurrentNodeId, defFlows } = createDefUnit();
        const initData = {
          nodeMap: defNodeMap,
          units: {
            nodes: [defNodeId],
          },
          flows: defFlows,
          currentNodeId: defCurrentNodeId,
        };
        setProjectState(initData);
        // 第一次新建完成要同步到后端，拿到当前项目唯一workSpaceUuid
        eventBus.emit(FlOW_PROJECT_INFO, UNIT_TYPE.NEW);
      }
    }
  } catch (err) {
    const { defNodeMap, defNodeId, defCurrentNodeId, defFlows } = createDefUnit();
    const initData = {
      nodeMap: defNodeMap,
      units: {
        nodes: [defNodeId],
      },
      flows: defFlows,
      currentNodeId: defCurrentNodeId,
    };
    setProjectState(initData);
    // 第一次新建完成要同步到后端，拿到当前项目唯一workSpaceUuid
    eventBus.emit(FlOW_PROJECT_INFO, UNIT_TYPE.NEW);
  }
};

const initFlowData = async (uuid) => {
  return await request('/api/canva/workspace-stream/list', {
    uuid,
    projectId: getProjectId(),
  });
};
